import type { MatchRecord } from '../hooks/useMatchRequests';

interface Props {
  row: MatchRecord;
  onClose: () => void;
}

const roleLabel: Record<string, string> = {
  patient: '환자/보호자',
  caregiver: '간병인',
};

function formatDate(iso: string | null) {
  if (!iso) return '-';
  const d = new Date(iso);
  return `${d.getFullYear()}년 ${d.getMonth() + 1}월 ${d.getDate()}일 ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function formatDateShort(iso: string | null) {
  if (!iso) return '-';
  const d = new Date(iso);
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
}

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div>
      <p className="text-xs text-gray-400 mb-0.5">{label}</p>
      <p className="text-sm font-medium text-gray-800">{value || <span className="text-gray-300">-</span>}</p>
    </div>
  );
}

function AmountField({ label, value }: { label: string; value: number | null | undefined }) {
  return (
    <div>
      <p className="text-xs text-gray-400 mb-0.5">{label}</p>
      <p className="text-sm font-semibold text-gray-800">{value != null ? `${value.toLocaleString()}원` : <span className="text-gray-300">-</span>}</p>
    </div>
  );
}

export default function MatchDetailModal({ row, onClose }: Props) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6" onClick={onClose}>
      <div
        className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10 rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#f0f9f6]">
              <i className="ri-hand-heart-line text-sm text-[#2E7D6B]"></i>
            </div>
            <div>
              <p className="text-xs text-gray-400">매칭번호</p>
              <p className="text-sm font-bold font-mono text-gray-900">{row.match_number || row.id.slice(0, 8)}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <StatusBadge status={row.status} />
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer transition-colors">
              <i className="ri-close-line text-gray-500"></i>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-6">
          {/* 매칭 기본 정보 */}
          <div className="grid grid-cols-2 gap-3 p-4 bg-gray-50 rounded-xl">
            <Field label="매칭 생성일" value={formatDate(row.created_at)} />
            <Field label="매칭 수락일" value={formatDate(row.accepted_at)} />
            <Field label="간병 완료일" value={row.care_end_date ? formatDateShort(row.care_end_date) : null} />
            <Field label="연락처 공개" value={row.contact_revealed ? '공개됨' : '미공개'} />
          </div>

          {/* 요청자 정보 */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className={`w-6 h-6 flex items-center justify-center rounded-md ${row.requester_role === 'caregiver' ? 'bg-[#E3F2FD]' : 'bg-[#FFF3E0]'}`}>
                <i className={`text-xs ${row.requester_role === 'caregiver' ? 'ri-nurse-line text-[#1976D2]' : 'ri-hospital-line text-[#F57C00]'}`}></i>
              </div>
              <h4 className="text-sm font-bold text-gray-900">
                요청자 정보 ({roleLabel[row.requester_role] || row.requester_role})
              </h4>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="성명" value={row.requester_name} />
              <Field label="연락처" value={row.requester_phone} />
            </div>
          </div>

          {/* 수신자 정보 */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className={`w-6 h-6 flex items-center justify-center rounded-md ${row.receiver_role === 'caregiver' ? 'bg-[#E3F2FD]' : 'bg-[#FFF3E0]'}`}>
                <i className={`text-xs ${row.receiver_role === 'caregiver' ? 'ri-nurse-line text-[#1976D2]' : 'ri-hospital-line text-[#F57C00]'}`}></i>
              </div>
              <h4 className="text-sm font-bold text-gray-900">
                수신자 정보 ({roleLabel[row.receiver_role] || row.receiver_role})
              </h4>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="성명" value={row.receiver_name} />
              <Field label="연락처" value={row.receiver_phone} />
            </div>
          </div>

          {/* 간병 조건 */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 flex items-center justify-center rounded-md bg-[#E8F5E9]">
                <i className="ri-calendar-check-line text-xs text-[#2E7D6B]"></i>
              </div>
              <h4 className="text-sm font-bold text-gray-900">간병 조건</h4>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="시작일" value={formatDateShort(row.start_date)} />
              <Field label="종료일" value={formatDateShort(row.end_date)} />
              <Field label="간병 장소" value={row.location} />
              <AmountField label="시간당 비용" value={row.hourly_rate} />
              <AmountField label="총 간병비" value={row.total_amount} />
              <Field label="하루 간병시간" value={row.daily_hours ? `${row.daily_hours}시간` : null} />
            </div>
          </div>

          {/* 요청사항 */}
          {row.notes && (
            <div>
              <h4 className="text-sm font-bold text-gray-900 mb-2">요청사항 / 메시지</h4>
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap">{row.notes}</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100">
          <button
            onClick={onClose}
            className="w-full py-2.5 rounded-xl text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 cursor-pointer transition-colors whitespace-nowrap"
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, { bg: string; text: string; label: string }> = {
    pending: { bg: 'bg-amber-50', text: 'text-amber-600', label: '대기중' },
    accepted: { bg: 'bg-green-50', text: 'text-green-600', label: '매칭완료' },
    rejected: { bg: 'bg-red-50', text: 'text-red-500', label: '거절됨' },
    completed: { bg: 'bg-blue-50', text: 'text-blue-600', label: '간병완료' },
    cancelled: { bg: 'bg-gray-100', text: 'text-gray-500', label: '취소됨' },
  };
  const s = styles[status] || styles.pending;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold ${s.bg} ${s.text}`}>
      {status === 'accepted' && <i className="ri-check-double-line text-[11px]" />}
      {s.label}
    </span>
  );
}